import React from 'react';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Chip from '@material-ui/core/Chip';
import { Box, Flex } from '@rebass/grid';
import get from 'lodash.get';
import { format } from 'date-fns';
import Skeleton from '@material-ui/lab/Skeleton';
import { useQuery } from 'react-query';
import { useParams } from '@reach/router';
import SideSheet from '../../organisim/side-sheet';
import UserAvatar from '../../atom/user-avatar';
import { useUserStats } from '../../../lib/hooks/use-user';
import { fetchGymUsers } from '../../../lib/fetchers/gyms';
import { fetchUserPromotions } from '../../../lib/fetchers/users';
import { colors } from '../../../config/theme';

// The rows from /user_promotions carry only name, email, avatar_url and belt, so
// there is no uid to fetch stats with and no program or belt size to show. One
// name-filtered request to the gym user list fills in the rest.
const MEMBER_LOOKUP_PARAMS = { perPage: 50 };

const StatRow = ({ label, value, isLoading, skeletonWidth = 40 }) => (
  <Flex justifyContent="space-between" alignItems="center" mb="10px">
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton variant="text" width={skeletonWidth} height={20} />
    ) : (
      <Typography variant="body1">{value}</Typography>
    )}
  </Flex>
);

const formatValue = (value, fallback = '—') => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return fallback;
  }
  return value;
};

const formatPromotedDate = (date) => {
  if (!date) {
    return '';
  }
  const promotedOn = new Date(date);
  return isNaN(promotedOn.getTime()) ? '' : format(promotedOn, 'MM/DD/YYYY');
};

const PromoHistoryEntry = ({ promo }) => (
  <Box mb="12px" p="12px" style={{ background: colors.surfaceAccent, borderRadius: '8px' }}>
    <Flex justifyContent="space-between" alignItems="center">
      <Typography variant="subtitle1">
        {promo.promotion_name || 'Promotion'}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {formatPromotedDate(promo.promoted_date)}
      </Typography>
    </Flex>
    <Box mt="8px">
      <Flex alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          label={`From: ${get(promo, 'belt_from.name') || 'No Belt'}`}
        />
        <Box mx="6px">→</Box>
        <Chip
          size="small"
          label={`To: ${get(promo, 'belt_to.name') || 'No Belt'}`}
        />
      </Flex>
    </Box>
  </Box>
);

export const PromotionMemberSheet = ({
  open,
  onClose = () => {},
  user = null,
}) => {
  const { gymUid } = useParams();
  const memberName = get(user, 'name');
  const inviteeUid = get(user, 'uid');

  // Nothing is requested until the sheet is open for a member that needs it.
  // The gym user list is fetched directly rather than through useGymUsers: that
  // hook debounces the search term for typeahead, which would fire one
  // unfiltered request for the whole first page before the name caught up.
  const needsMemberLookup = open && !!memberName && !inviteeUid && !!gymUid;
  const { data: memberSearch, isLoading: isLoadingMember } = useQuery(
    ['gym-users', 'member-lookup', gymUid, memberName],
    () => fetchGymUsers(gymUid, memberName, MEMBER_LOOKUP_PARAMS),
    {
      enabled: needsMemberLookup,
      refetchOnWindowFocus: false,
    }
  );

  // The name search is a LIKE match, so it can come back with several members.
  const matchedMember = get(memberSearch, 'users', []).find(
    (result) => String(result.id) === String(get(user, 'user_id')),
  );
  const member = matchedMember || user;
  const uid = inviteeUid || get(matchedMember, 'uid');
  const { stats = {}, isLoading: isLoadingStats } = useUserStats(uid);

  // /users/:id/promotions resolves either a uid or a numeric id, so history can
  // be fetched straight from the invitee row without waiting on the lookup. It
  // gets its own query key: useUserPromotions in use-user-promotions.js and the
  // one in use-promotions.js both cache under ['user-promotions', id] off
  // different id spaces, and both pages rendering this sheet use the latter.
  const memberId = get(user, 'user_id') || uid;
  const { data: promoHistory, isLoading: isLoadingHistory } = useQuery(
    ['user-promotion-history', memberId],
    () => fetchUserPromotions(memberId),
    {
      enabled: open && !!memberId,
      refetchOnWindowFocus: false,
    }
  );
  const promotions = get(promoHistory, 'promotions', []);

  // A promoted invitee's belt is the one they were promoted from, which is the
  // more useful value here, so it wins over the member record's current belt.
  const rank = get(user, 'belt.name') || get(member, 'belt.name');

  // The member record carries the program and belt size; the stats request can
  // only start once that lookup has produced a uid. A lookup that finishes
  // without a match leaves both false, so the rows show their placeholders
  // rather than skeletons that would never resolve.
  const isResolvingMember = needsMemberLookup && isLoadingMember;
  const isLoadingStatFields = isResolvingMember || (!!uid && isLoadingStats);

  return (
    <SideSheet
      open={open}
      onClose={onClose}
      title={get(user, 'name', 'Member details')}
    >
      <Card>
        <CardContent>
          <Flex alignItems="center" mb="16px">
            <Box mr="16px">
              <UserAvatar src={get(user, 'avatar_url')} />
            </Box>
            <Box>
              <Typography variant="h6">{formatValue(get(user, 'name'))}</Typography>
              <Typography variant="body2" color="textSecondary">
                {formatValue(get(user, 'email') || get(member, 'email'))}
              </Typography>
            </Box>
          </Flex>

          <Divider />

          <Box mt="16px">
            <Typography variant="subtitle1" gutterBottom>
              Basic info
            </Typography>
            <StatRow
              label="Program"
              value={formatValue(get(member, 'gym_program.name'))}
              isLoading={isResolvingMember}
              skeletonWidth={90}
            />
            <StatRow label="Rank" value={formatValue(rank)} />
            <StatRow
              label="Belt Size"
              value={formatValue(get(member, 'belt_size.name'))}
              isLoading={isResolvingMember}
              skeletonWidth={60}
            />
          </Box>

          <Divider />

          <Box mt="16px">
            <Typography variant="subtitle1" gutterBottom>
              Stats
            </Typography>
            <StatRow
              label="Active days since last promotion"
              value={formatValue(
                get(stats, 'meta.active_days_since_last_promotion'),
                '0',
              )}
              isLoading={isLoadingStatFields}
              skeletonWidth={32}
            />
          </Box>

          <Divider />

          <Box mt="16px">
            <Typography variant="subtitle1" gutterBottom>
              Promotion history
            </Typography>
            {isLoadingHistory ? (
              <>
                <Skeleton
                  variant="rect"
                  height={72}
                  style={{ borderRadius: '8px', marginBottom: '12px' }}
                />
                <Skeleton
                  variant="rect"
                  height={72}
                  style={{ borderRadius: '8px' }}
                />
              </>
            ) : promotions.length > 0 ? (
              promotions.map((promo) => (
                <PromoHistoryEntry key={promo.id} promo={promo} />
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No promotion history
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </SideSheet>
  );
};

export default PromotionMemberSheet;
